import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Clock, Users, Trash2, ChevronRight, Zap, ArrowUpDown, Flag, Loader2, Eye, LayoutList, FolderTree, Trophy, Radio, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getCategoryColor } from '../utils/categoryColors';
import LiveSessionCard from './LiveSessionCard';
import PurgeOldSessions from './PurgeOldSessions';
import { API } from '../config/api';

// Derive the BROAD category (Turismo Pista, Turismo Nacional, Rotax,
// Carshow, etc.) for a race so the tree groups sessions the way the
// admin panel does — never by class (C2, CLASE 3, MASTER MAX, etc.).
//
// Order of preference:
//   1. Admin-configured Speedhive category matched by
//      `race.speedhive_live_event_id → category.live_event_id`. This
//      wins when set because it uses the exact name the admin chose
//      (e.g. "APTP - Turismo Pista", "Turismo Nacional APAT").
//   2. The DB `race.category` slug, prettified
//      (`turismo_pista` → "TURISMO PISTA"). Populated by uploaders /
//      importers when they classify the file.
//   3. Fallback to "SIN CATEGORÍA".
//
// Deliberately DO NOT parse `speedhive_session_name`, `_group_name`
// or `race.name` — those hold the class/session label ("CLASE 2",
// "MASTER MAX", "5ta TANDA MINI MAX") and using them promotes classes
// to the top level, which is what the user asked us to stop doing.
const deriveClase = (race, categoryByLeid = null) => {
  const leid = (race.speedhive_live_event_id || '').trim();
  if (leid && categoryByLeid) {
    const adminName = categoryByLeid.get(leid);
    if (adminName) return adminName;
  }
  const cat = (race.category || '').trim();
  if (cat) return cat.replace(/_/g, ' ').toUpperCase();
  return 'SIN CATEGORÍA';
};

const RaceList = ({ races, racesLoaded = true, onSelectRace, onDeleteRace, onRacesChanged, loading, canDelete = false }) => {
  const [sortBy, setSortBy] = useState('date');
  // View mode: 'cards' (default landing — category tiles like OrgaLaps),
  // 'tree' (Category → Date → Session hierarchy), or 'list' (flat).
  // `cards` shows only one card per registered category (admin + Pro
  // user's personal) with a session count; clicking drills into the
  // filtered tree.
  const [viewMode, setViewMode] = useState('cards');
  const [selectedClase, setSelectedClase] = useState(null);
  const [listLimit, setListLimit] = useState(LIST_PAGE);

  // Fetch admin-configured Speedhive categories once on mount. Used to
  // resolve `race.speedhive_live_event_id → category.name` so the tree
  // groups match exactly what the admin panel shows. Silent fallback:
  // if the endpoint fails, the tree uses the parsed-clase heuristics
  // instead.
  const [adminCategories, setAdminCategories] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/speedhive/categories`);
        if (!cancelled) setAdminCategories(Array.isArray(r.data) ? r.data : []);
      } catch (_) {
        // Non-fatal — heuristics take over.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categoryByLeid = useMemo(() => {
    const m = new Map();
    for (const c of adminCategories || []) {
      const leid = (c.live_event_id || '').trim();
      if (leid && c.name) m.set(leid, c.name);
    }
    return m;
  }, [adminCategories]);

  const extractRaceNumber = (name) => {
    const match = name.match(/(?:Carrera|Race|Fecha|C)\s*(\d+)/i);
    return match ? parseInt(match[1]) : 999;
  };

  // "in progress" flag: prefer the explicit `status` field written by AutoLive
  // (F2 fix). Fall back to the historical name-matching heuristic only when
  // no status is present (manual imports, google drive, legacy docs).
  const isCompleted = (race) => {
    if (race.status === 'in_progress') return false;
    if (race.status === 'finalized') return true;
    const upper = (race.name || '').toUpperCase();
    return upper.includes('FINAL') || upper.includes('CLASIFICACION') || upper.includes('CLASIFICACIÓN');
  };

  const sortedRaces = useMemo(() => {
    const sorted = [...races];
    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => {
          const numA = extractRaceNumber(a.name);
          const numB = extractRaceNumber(b.name);
          if (numA !== numB) return numA - numB;
          return a.name.localeCompare(b.name);
        });
      case 'drivers':
        return sorted.sort((a, b) => b.driver_count - a.driver_count);
      case 'date':
      default:
        return sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
    }
  }, [races, sortBy]);

  // Cross-cutting: live sessions (surfaced on top of the list). A race is
  // live when the AutoLive poller is still writing to it — the poller sets
  // `source === 'speedhive_live_auto'` and clears `status === 'finalized'`
  // on completion. Newest first so a fresh session takes precedence over an
  // older stalled one.
  const liveRaces = useMemo(() => {
    return races
      .filter((r) => r.source === 'speedhive_live_auto' && r.status !== 'finalized')
      .sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date));
  }, [races]);

  // Build a Class > Event > Session tree from ALL races.
  //
  // Level 1 uses `deriveClase(race)` — the class parsed from the session
  // title — instead of the DB `category` slug. This keeps things clean
  // when the same organizer times two classes in the same weekend (e.g.
  // TN Clase 2 and TN Clase 3 both under `turismo_nacional`).
  //
  // Sort order: everything by most-recent-date desc — classes sort by
  // their newest race, events within a class sort by their newest race,
  // and sessions inside an event sort by created_at (or date) desc so
  // the freshest capture is at the top.
  const tree = useMemo(() => {
    const raceTs = (r) => {
      const t = new Date(r.created_at || r.date || 0).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    /** @type {Map<string, Map<string, any[]>>} */
    // Level 1: Clase/Categoría · Level 2: Fecha (YYYY-MM-DD) · Level 3: Tanda.
    // User spec (Feb 28 2026): navegación jerárquica por Categoría → Fecha
    // → Tanda para navegar fines de semana con múltiples días de
    // entrenamiento/clasificación/final.
    const byClase = new Map();
    // Derive the ISO date key ("YYYY-MM-DD") from `race.date` first, then
    // fall back to `created_at`. Speedhive AutoLive always populates
    // `date` from the event feed, so this is normally accurate.
    const raceDateKey = (r) => {
      const raw = r.date || r.created_at;
      if (!raw) return 'sin-fecha';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return 'sin-fecha';
      return d.toISOString().slice(0, 10);
    };
    for (const r of races) {
      const clase = deriveClase(r, categoryByLeid) || 'SIN CATEGORIA';
      const dateKey = raceDateKey(r);
      if (!byClase.has(clase)) byClase.set(clase, new Map());
      const byDate = byClase.get(clase);
      if (!byDate.has(dateKey)) byDate.set(dateKey, []);
      byDate.get(dateKey).push(r);
    }
    const claseLatest = new Map();
    for (const [clase, byDate] of byClase.entries()) {
      let claseMax = 0;
      const dateEntries = Array.from(byDate.entries()).map(([dk, arr]) => {
        arr.sort((a, b) => raceTs(b) - raceTs(a));
        const dMax = arr.length ? raceTs(arr[0]) : 0;
        if (dMax > claseMax) claseMax = dMax;
        return { dk, arr, dMax };
      });
      // Ordenar fechas por la sesión más reciente dentro de esa fecha,
      // desc (fin de semana actual arriba, fechas viejas al final).
      dateEntries.sort((a, b) => b.dMax - a.dMax);
      const sortedByDate = new Map();
      for (const e of dateEntries) sortedByDate.set(e.dk, e.arr);
      byClase.set(clase, sortedByDate);
      claseLatest.set(clase, claseMax);
    }
    const orderedClases = Array.from(byClase.keys()).sort(
      (a, b) => (claseLatest.get(b) || 0) - (claseLatest.get(a) || 0)
    );
    const ordered = new Map();
    for (const c of orderedClases) ordered.set(c, byClase.get(c));
    return { byClase: ordered };
  }, [races, categoryByLeid]);

  const hasTreeContent = tree.byClase.size > 0;
  const getClase = React.useCallback((r) => deriveClase(r, categoryByLeid) || 'SIN CATEGORIA', [categoryByLeid]);

  // Client-side search across class name, date label and session name.
  // Empty query = show everything. Case-insensitive substring match.
  const [treeQuery, setTreeQuery] = useState('');
  const filteredTree = useMemo(() => {
    const q = treeQuery.trim().toLowerCase();
    if (!q) return tree;
    const out = new Map();
    for (const [clase, byDate] of tree.byClase.entries()) {
      const claseMatch = clase.toLowerCase().includes(q);
      const filteredDates = new Map();
      for (const [dk, sessions] of byDate.entries()) {
        const dateMatch = dk.toLowerCase().includes(q);
        const matchingSessions = claseMatch || dateMatch
          ? sessions
          : sessions.filter((r) => {
              const label = [
                r.speedhive_event_name,
                r.speedhive_group_name,
                r.speedhive_session_name,
                r.name,
              ].filter(Boolean).join(' ').toLowerCase();
              return label.includes(q);
            });
        if (matchingSessions.length) filteredDates.set(dk, matchingSessions);
      }
      if (filteredDates.size) out.set(clase, filteredDates);
    }
    return { byClase: out };
  }, [tree, treeQuery]);

  // Card gallery: one card per category. Uses the same grouping as the
  // tree so counts match. Query filters the visible cards by name.
  const [cardQuery, setCardQuery] = useState('');
  const categoryCards = useMemo(() => {
    const q = cardQuery.trim().toLowerCase();
    const cards = [];
    for (const [clase, byDate] of tree.byClase.entries()) {
      if (q && !clase.toLowerCase().includes(q)) continue;
      const sessions = [];
      const dateKeys = Array.from(byDate.keys());
      for (const dk of dateKeys) sessions.push(...byDate.get(dk));
      // Newest & oldest session for the date-range badge on the card.
      const times = sessions
        .map((r) => new Date(r.created_at || r.date || 0).getTime())
        .filter((t) => Number.isFinite(t) && t > 0)
        .sort((a, b) => a - b);
      cards.push({
        clase,
        count: sessions.length,
        days: dateKeys.length,
        earliest: times[0] || null,
        latest: times[times.length - 1] || null,
        color: getCategoryColor(clase),
        // How many sessions are live right now?
        liveCount: sessions.filter(
          (r) => r.source === 'speedhive_live_auto' && r.status !== 'finalized',
        ).length,
      });
    }
    return cards;
  }, [tree, cardQuery]);

  if (races.length === 0) {
    // Distinguish "first fetch still pending" from "really no races".
    // The empty-state message used to flash on fresh mounts (coming
    // back from /live) because `fetchRaces` hadn't yet responded —
    // which users read as "the app forgot my data, it's slow".
    if (!racesLoaded) {
      return (
        <Card className="bg-neutral-900 border-red-900/30" data-testid="racelist-skeleton">
          <CardContent className="py-16 text-center">
            <Loader2 className="w-10 h-10 mx-auto text-red-500/70 mb-4 animate-spin" />
            <p className="text-gray-400 mb-1">Cargando carreras…</p>
            <p className="text-sm text-gray-500">Recuperando tus sesiones guardadas</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-16 text-center">
          <Clock className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 mb-2">No hay carreras disponibles</p>
          <p className="text-sm text-gray-500">Sube un archivo para comenzar</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Zap className="w-5 h-5 text-red-500" />
          Sesiones
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          {hasTreeContent && (
            <div className="inline-flex items-center gap-0.5 rounded-md border border-neutral-700 bg-neutral-900/60 p-0.5" data-testid="racelist-view-toggle">
              <button
                onClick={() => { setViewMode('cards'); setSelectedClase(null); }}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors inline-flex items-center gap-1 ${
                  viewMode === 'cards'
                    ? 'bg-neutral-700 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="racelist-view-cards"
                title="Tarjetas por categoría"
              >
                <FolderTree className="w-3.5 h-3.5" />
                Tarjetas
              </button>
              <button
                onClick={() => { setViewMode('tree'); setSelectedClase(null); }}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors inline-flex items-center gap-1 ${
                  viewMode === 'tree'
                    ? 'bg-neutral-700 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="racelist-view-tree"
                title="Categoría › Fecha › Tanda"
              >
                <FolderTree className="w-3.5 h-3.5" />
                Jerarquía
              </button>
              <button
                onClick={() => { setViewMode('list'); setSelectedClase(null); }}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors inline-flex items-center gap-1 ${
                  viewMode === 'list'
                    ? 'bg-neutral-700 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="racelist-view-list"
                title="Vista lista plana"
              >
                <LayoutList className="w-3.5 h-3.5" />
                Lista
              </button>
            </div>
          )}
          {viewMode === 'list' && (
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-3.5 h-3.5 text-gray-500" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px] h-8 bg-neutral-800 border-neutral-700 text-white text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-700">
                  <SelectItem value="date" className="text-white hover:bg-neutral-800 text-xs">Fecha (reciente)</SelectItem>
                  <SelectItem value="name" className="text-white hover:bg-neutral-800 text-xs">Nombre</SelectItem>
                  <SelectItem value="drivers" className="text-white hover:bg-neutral-800 text-xs">N° de Pilotos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {canDelete && (
            <PurgeOldSessions
              races={races}
              categories={Array.from(tree.byClase.keys())}
              getClase={getClase}
              onDone={onRacesChanged}
            />
          )}
          <span className="text-xs text-gray-500 bg-neutral-800 px-2 py-1 rounded">
            {races.length}
          </span>
        </div>
      </div>

      {/* Live sessions surface — shown above both list & tree views. */}
      {liveRaces.length > 0 && (
        <div className="space-y-2" data-testid="live-sessions-strip">
          {liveRaces.map((race) => (
            <LiveSessionCard
              key={race.id}
              race={race}
              onSelectRace={onSelectRace}
              loading={loading}
            />
          ))}
        </div>
      )}

      {/* Search box — filters the tree by class/event/session name. */}
      {viewMode === 'tree' && hasTreeContent && (
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={treeQuery}
            onChange={(e) => setTreeQuery(e.target.value)}
            placeholder="Buscar por clase, evento o sesión…"
            className="w-full h-9 pl-8 pr-8 rounded-md bg-neutral-900 border border-neutral-800 text-sm text-white placeholder-gray-500 focus:border-red-600 focus:outline-none focus:ring-1 focus:ring-red-600/40 transition-colors"
            data-testid="race-tree-search"
          />
          {treeQuery && (
            <button
              onClick={() => setTreeQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs"
              data-testid="race-tree-search-clear"
              aria-label="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Cards search: only when at the top level (no category selected). */}
      {viewMode === 'cards' && !selectedClase && hasTreeContent && (
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={cardQuery}
            onChange={(e) => setCardQuery(e.target.value)}
            placeholder="Buscar categoría…"
            className="w-full h-9 pl-8 pr-8 rounded-md bg-neutral-900 border border-neutral-800 text-sm text-white placeholder-gray-500 focus:border-red-600 focus:outline-none focus:ring-1 focus:ring-red-600/40 transition-colors"
            data-testid="race-card-search"
          />
          {cardQuery && (
            <button
              onClick={() => setCardQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs"
              aria-label="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* CARDS · TREE · LIST */}
      {viewMode === 'cards' && !selectedClase && (
        <CategoryCards
          cards={categoryCards}
          onSelect={(clase) => setSelectedClase(clase)}
        />
      )}
      {viewMode === 'cards' && selectedClase && (
        <div className="space-y-3">
          <button
            onClick={() => setSelectedClase(null)}
            className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
            data-testid="race-card-back"
          >
            <ChevronRight className="w-3.5 h-3.5 rotate-180" />
            Volver a categorías
          </button>
          <RaceTree
            tree={{
              byClase: new Map(
                tree.byClase.has(selectedClase)
                  ? [[selectedClase, tree.byClase.get(selectedClase)]]
                  : [],
              ),
            }}
            isCompleted={isCompleted}
            onSelectRace={onSelectRace}
            onDeleteRace={onDeleteRace}
            loading={loading}
            canDelete={canDelete}
          />
        </div>
      )}
      {viewMode === 'tree' && (
        <RaceTree
          tree={filteredTree}
          isCompleted={isCompleted}
          onSelectRace={onSelectRace}
          onDeleteRace={onDeleteRace}
          loading={loading}
          canDelete={canDelete}
        />
      )}
      {viewMode === 'list' && (
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden divide-y divide-neutral-800/60" data-testid="race-list">
          {sortedRaces.slice(0, listLimit).map((race) => {
            const completed = isCompleted(race);
            return (
              <div
                key={race.id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-800/40 transition-colors group"
                data-testid={`race-card-${race.id}`}
              >
              {/* Status indicator. Green dot = analizable (has lap_details).
                  Blue dot = view-only (grilla / plantilla general).
                  Yellow pulse = event currently in progress. */}
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  !completed
                    ? 'bg-yellow-500 animate-pulse'
                    : race.is_analyzable
                      ? 'bg-green-500'
                      : 'bg-sky-500'
                }`}
                title={
                  !completed
                    ? 'En curso'
                    : race.is_analyzable
                      ? 'Analizable — contiene datos vuelta por vuelta'
                      : 'Solo visualización — plantilla o grilla general (sin parciales por vuelta)'
                }
              />

              {/* Race info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white truncate group-hover:text-red-400 transition-colors">
                    {race.name}
                  </span>
                  {!completed && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 px-1.5 py-0.5 rounded flex-shrink-0">
                      En curso
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {race.driver_count}
                  </span>
                  <span>
                    {new Date(race.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {race.category && (
                    <span className="text-gray-600">{race.category.replace(/_/g, ' ')}</span>
                  )}
                </div>
              </div>

              {/* Actions. "Analizar" (green, primary) shows only when the
                  session has real lap-by-lap data. "Visualizar" (neutral)
                  covers grillas/plantillas that only show final positions —
                  they'd break the analysis UI (no sectors, no lap details). */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button
                  onClick={() => onSelectRace(race)}
                  size="sm"
                  className={
                    race.is_analyzable
                      ? "bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-3"
                      : "bg-neutral-800 hover:bg-neutral-700 text-gray-200 border border-neutral-700 text-xs h-7 px-3"
                  }
                  disabled={loading}
                  title={
                    race.is_analyzable
                      ? 'Analizar vuelta por vuelta con parciales y comparativa'
                      : 'Este archivo no contiene datos por vuelta (VtaxVta) — sólo visualización de resultados'
                  }
                  data-testid={`select-race-${race.id}`}
                >
                  {loading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : race.is_analyzable ? (
                    <>Analizar <ChevronRight className="w-3 h-3 ml-1" /></>
                  ) : (
                    <><Eye className="w-3 h-3 mr-1" /> Visualizar</>
                  )}
                </Button>
                {canDelete && (
                  <Button
                    onClick={(e) => { e.stopPropagation(); onDeleteRace(race.id); }}
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-gray-600 hover:text-red-400 hover:bg-red-900/20"
                    data-testid={`delete-race-${race.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
          {sortedRaces.length > listLimit && (
            <ShowMoreButton
              testId="race-list-show-more"
              remaining={sortedRaces.length - listLimit}
              onClick={() => setListLimit((n) => n + LIST_PAGE)}
              label="sesiones"
            />
          )}
      </div>
      )}
    </div>
  );
};

const LIST_PAGE = 50;
const DATE_PAGE = 6;

const ShowMoreButton = ({ testId, remaining, onClick, label }) => (
  <button
    onClick={onClick}
    className="w-full py-2.5 text-xs font-medium text-gray-400 hover:text-white hover:bg-neutral-800/50 transition-colors inline-flex items-center justify-center gap-1.5"
    data-testid={testId}
  >
    <ChevronRight className="w-3.5 h-3.5 rotate-90" />
    Ver más ({remaining} {label} restantes)
  </button>
);

// ---------------------------------------------------------------------------
// Hierarchical view: Category > Fecha > Tanda (Feb 28 2026 restructure).
// ---------------------------------------------------------------------------
const RaceTree = ({ tree, isCompleted, onSelectRace, onDeleteRace, loading, canDelete = false }) => {
  const { byClase } = tree;
  const [dateLimits, setDateLimits] = useState({});
  const clasesOrdered = Array.from(byClase.keys());
  if (clasesOrdered.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-gray-500 bg-neutral-900 border border-neutral-800 rounded-xl" data-testid="race-tree-empty">
        No hay resultados que coincidan con la búsqueda.
      </div>
    );
  }
  // Format an ISO date key ("2026-02-28") for humans. "sin-fecha" is
  // used as a sentinel when a race has neither `date` nor `created_at`.
  const fmtDate = (dk) => {
    if (dk === 'sin-fecha') return 'Sin fecha';
    const d = new Date(dk + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return dk;
    return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };
  return (
    <div className="space-y-3" data-testid="race-tree">
      {clasesOrdered.map((clase) => {
        const col = getCategoryColor(clase);
        const byDate = byClase.get(clase);
        const dateKeys = Array.from(byDate.keys());
        const dateLimit = dateLimits[clase] || DATE_PAGE;
        const sessionCount = Array.from(byDate.values()).reduce((s, arr) => s + arr.length, 0);
        return (
          <div
            key={clase}
            className={`rounded-xl border ${col.border} bg-neutral-900 overflow-hidden`}
            data-testid={`race-tree-category-${clase}`}
          >
            <div className={`px-4 py-2.5 ${col.bg} border-b ${col.border} flex items-center gap-2`}>
              <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
              <span className={`text-sm font-semibold ${col.text} uppercase tracking-wide`}>
                {clase.replace(/_/g, ' ')}
              </span>
              <span className="text-[10px] text-gray-500 ml-1">
                · {dateKeys.length} {dateKeys.length === 1 ? 'día' : 'días'} · {sessionCount} tandas
              </span>
            </div>
            <div className="divide-y divide-neutral-800/60">
              {dateKeys.slice(0, dateLimit).map((dk) => {
                const sessions = byDate.get(dk);
                return (
                  <div key={dk} className="px-4 py-2" data-testid={`race-tree-date-${dk}`}>
                    <div className="flex items-center gap-1.5 text-xs text-gray-300 font-medium mb-1.5">
                      <Clock className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                      <span className="truncate">{fmtDate(dk)}</span>
                      <span className="text-gray-600 text-[10px]">
                        · {sessions.length} {sessions.length === 1 ? 'tanda' : 'tandas'}
                      </span>
                    </div>
                    <div className="ml-5 space-y-1">
                      {sessions.map((race) => (
                        <TreeSessionRow
                          key={race.id}
                          race={race}
                          isCompleted={isCompleted}
                          onSelectRace={onSelectRace}
                          onDeleteRace={onDeleteRace}
                          loading={loading}
                          canDelete={canDelete}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
              {dateKeys.length > dateLimit && (
                <ShowMoreButton
                  testId={`race-tree-show-more-${clase.replace(/[^A-Za-z0-9]/g, '_')}`}
                  remaining={dateKeys.length - dateLimit}
                  onClick={() => setDateLimits((m) => ({ ...m, [clase]: dateLimit + DATE_PAGE }))}
                  label={dateKeys.length - dateLimit === 1 ? 'fecha' : 'fechas'}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TreeSessionRow = ({ race, isCompleted, onSelectRace, onDeleteRace, loading, showCategoryHint, canDelete = false }) => {
  const completed = isCompleted(race);
  // Tanda label — prefer Speedhive's session-type label ("5ta TANDA
  // MASTER NAC", "Clasificación", "Final"). Fall back to auto-composed
  // "[Categoría] - [YYYY-MM-DD] - [Tipo]" so we never show duplicate
  // generic names for legacy XLS/HTML imports.
  const speedhiveLabel = [race.speedhive_group_name, race.speedhive_session_name]
    .filter(Boolean)
    .join(' · ');
  let sessionLabel = speedhiveLabel;
  if (!sessionLabel) {
    // Auto-name: [Category] - [Date] - [SessionType from name tail]
    const catPart = (race.category || 'Sesión').replace(/_/g, ' ');
    const datePart = race.date
      ? new Date(race.date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '';
    const nameParts = (race.name || '').split(/\s*-\s*/).filter(Boolean);
    const typeTail = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    sessionLabel = [catPart, datePart, typeTail].filter(Boolean).join(' - ') || race.name;
  }
  const isLive = race.source === 'speedhive_live_auto' && !completed;
  return (
    <div
      className="flex items-center gap-2 py-1.5 pl-2 pr-2 rounded hover:bg-neutral-800/40 transition-colors group text-sm"
      data-testid={`race-tree-row-${race.id}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
          isLive
            ? 'bg-red-500 animate-pulse'
            : !completed
              ? 'bg-yellow-500'
              : race.is_analyzable
                ? 'bg-green-500'
                : 'bg-sky-500'
        }`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-white truncate">{sessionLabel}</span>
          {isLive && (
            <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase bg-red-500/15 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded flex-shrink-0">
              <Radio className="w-2.5 h-2.5" />
              En curso
            </span>
          )}
          {showCategoryHint && race.category && (
            <span className="text-[10px] text-gray-500">
              · {race.category.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 truncate">
          <span className="inline-flex items-center gap-0.5 flex-shrink-0">
            <Users className="w-3 h-3" />
            {race.driver_count}
          </span>
          {race.speedhive_event_name && (
            <span className="inline-flex items-center gap-0.5 truncate">
              <Trophy className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{race.speedhive_event_name}</span>
            </span>
          )}
        </div>
      </div>
      <Button
        onClick={() => onSelectRace(race)}
        size="sm"
        className={
          race.is_analyzable
            ? 'bg-green-600 hover:bg-green-700 text-white text-xs h-6 px-2'
            : 'bg-neutral-800 hover:bg-neutral-700 text-gray-200 border border-neutral-700 text-xs h-6 px-2'
        }
        disabled={loading}
        data-testid={`tree-select-race-${race.id}`}
      >
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : race.is_analyzable ? (
          <>Analizar<ChevronRight className="w-3 h-3 ml-0.5" /></>
        ) : (
          <><Eye className="w-3 h-3 mr-0.5" />Ver</>
        )}
      </Button>
      {canDelete && (
        <Button
          onClick={(e) => { e.stopPropagation(); onDeleteRace(race.id); }}
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-gray-600 hover:text-red-400 hover:bg-red-900/20"
          data-testid={`tree-delete-race-${race.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CategoryCards — landing gallery of registered categories.
// One tile per category (admin-configured Speedhive + Pro-user "Mis
// categorías MyLaps"). Shows the total session count, the day count
// and the freshest-session date badge (like OrgaLaps' event tiles).
// Clicking a card lifts `selectedClase` upstream so the parent shows
// the RaceTree filtered to that class.
// ---------------------------------------------------------------------------
const CategoryCards = ({ cards, onSelect }) => {
  if (cards.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-gray-500 bg-neutral-900 border border-neutral-800 rounded-xl" data-testid="race-cards-empty">
        No hay categorías que coincidan con la búsqueda.
      </div>
    );
  }
  const fmt = (t) => {
    if (!t) return '—';
    return new Date(t).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="race-cards-grid">
      {cards.map((c) => (
        <button
          key={c.clase}
          onClick={() => onSelect(c.clase)}
          className={`text-left rounded-xl border ${c.color.border} bg-neutral-900 hover:bg-neutral-900/60 transition-all p-4 relative overflow-hidden group`}
          data-testid={`race-card-${c.clase.replace(/[^A-Za-z0-9]/g, '_')}`}
        >
          {/* Category color bar accent along the left edge. */}
          <span className={`absolute left-0 top-0 bottom-0 w-1 ${c.color.dot}`} aria-hidden />
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className={`text-[10px] uppercase tracking-widest ${c.color.text} font-semibold mb-1`}>
                {c.days} {c.days === 1 ? 'día' : 'días'} · {fmt(c.earliest)} — {fmt(c.latest)}
              </div>
              <h3 className="text-lg font-bold text-white leading-tight truncate">
                {c.clase.replace(/_/g, ' ')}
              </h3>
            </div>
            {c.liveCount > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase bg-red-500/20 text-red-400 border border-red-500/40 px-1.5 py-0.5 rounded flex-shrink-0"
                title={`${c.liveCount} en vivo`}
              >
                <Radio className="w-2.5 h-2.5 animate-pulse" />
                Live
              </span>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-neutral-800/60 flex items-center justify-between text-xs">
            <span className="inline-flex items-center gap-1.5 text-gray-300">
              <Zap className="w-3.5 h-3.5 text-gray-500" />
              <strong className="text-white text-sm">{c.count}</strong>
              {c.count === 1 ? 'sesión guardada' : 'sesiones guardadas'}
            </span>
            <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
          </div>
        </button>
      ))}
    </div>
  );
};

export default RaceList;
